import React from 'react';
import { ordersApi } from '@/services/api';
import { formatDate, formatCurrency, formatNumber } from '@/utils/formatters';
import { CompletedOrder, AggregatedOrder } from '@/components/orders/types';
import BaseCompletedOrders, { CompletedSummary } from '@/components/orders/BaseCompletedOrders';
import TableWrapper from '@/components/orders/TableWrapper';
import styles from '@/components/orders/OrderManagement.module.css';
import shared from '@/components/utils/shared.module.css';
import OrderStatusPill from '@/components/orders/orderStatus';

interface EquityCompletedData {
  aggregated: AggregatedOrder[];
  nri: CompletedOrder[];
}

const fetchSide = async (
  fetcher: (p: object) => Promise<{
    aggregated_non_nri?: AggregatedOrder[];
    nri_orders?: CompletedOrder[];
    count: number;
    total_quantity: number;
    total_amount: string;
    currency?: string | null;
  }>,
): Promise<{ summary: CompletedSummary; data: EquityCompletedData }> => {
  const res = await fetcher({ page_size: 100, aggregate_non_nri: true });
  return {
    summary: {
      totalCount: res.count,
      totalQty: res.total_quantity,
      totalAmt: res.total_amount,
      currency: res.currency,
    },
    data: {
      aggregated: res.aggregated_non_nri ?? [],
      nri: res.nri_orders ?? [],
    },
  };
};

const renderSubSections = (data: EquityCompletedData) => (
  <>
    {data.aggregated.length > 0 && (
      <div className={styles['sub-section']}>
        <div className={styles['sub-section-header']}>
          <div className={styles['sub-section-line']} />
          <span className={styles['badge-non-nri']}>Non-NRI — Aggregated</span>
          <div className={styles['sub-section-line']} />
        </div>
        <TableWrapper>
          <table className={`${shared.table} ${styles.table}`}>
            <thead>
              <tr>
                <th>Scheme</th>
                <th>Company</th>
                <th className={styles.num}>Orders</th>
                <th className={styles.num}>Total Qty</th>
                <th className={styles.num}>Total Amount</th>
                <th>Broker</th>
              </tr>
            </thead>
            <tbody>
              {data.aggregated.map(order => (
                <tr key={`${order.scheme.id}-${order.company.id}-${order.order_id}`}>
                  <td>{order.scheme.code}</td>
                  <td>{order.company.symbol}</td>
                  <td className={styles.num}>{order.order_count}</td>
                  <td className={`${styles['text-mono']} ${styles.num}`}>{formatNumber(order.total_quantity, 0)}</td>
                  <td className={`${styles['text-mono']} ${styles.num}`}>{formatCurrency(order.total_amount, order.scheme.currency)}</td>
                  <td>{order.broker ? order.broker.code : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrapper>
      </div>
    )}

    {data.nri.length > 0 && (
      <div className={styles['sub-section']}>
        <div className={styles['sub-section-header']}>
          <div className={styles['sub-section-line']} />
          <span className={styles['badge-nri']}>NRI — Individual</span>
          <div className={styles['sub-section-line']} />
        </div>
        <TableWrapper>
          <table className={`${shared.table} ${styles.table}`}>
            <thead>
              <tr>
                <th>Order Date</th>
                <th>Scheme</th>
                <th>Company</th>
                <th>Client Code</th>
                <th>Client Name</th>
                <th className={styles.num}>Quantity</th>
                <th className={styles.num}>Amount</th>
                <th>Broker</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.nri.map(order => (
                <tr key={order.id}>
                  <td>{formatDate(order.order_date)}</td>
                  <td>{order.scheme.code}</td>
                  <td>{order.company.symbol}</td>
                  <td>{order.client.code}</td>
                  <td>{order.client.name}</td>
                  <td className={`${styles['text-mono']} ${styles.num}`}>{formatNumber(order.quantity, 0)}</td>
                  <td className={`${styles['text-mono']} ${styles.num}`}>{formatCurrency(order.amount, order.scheme.currency)}</td>
                  <td>{order.broker ? order.broker.code : '—'}</td>
                  <td><OrderStatusPill stage="completed" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrapper>
      </div>
    )}
  </>
);

const CompletedOrders: React.FC = () => (
  <BaseCompletedOrders<EquityCompletedData, EquityCompletedData>
    fetchBuyOrders={() => fetchSide(ordersApi.getCompletedBuyOrders)}
    fetchSellOrders={() => fetchSide(ordersApi.getCompletedSellOrders)}
    renderBuySubSections={renderSubSections}
    renderSellSubSections={renderSubSections}
    isBuyEmpty={(d) => d.aggregated.length === 0 && d.nri.length === 0}
    isSellEmpty={(d) => d.aggregated.length === 0 && d.nri.length === 0}
  />
);

export default CompletedOrders;